import { useEffect, useRef, useState } from 'react'
import { ref, update } from 'firebase/database'
import { database } from '../services/firebase'
import { subscribeToMatch } from '../services/firebase'

interface UseTennisMatchLogicProps {
  matchId: string
  team1Color: string
  team2Color: string
  isController: boolean
  showQRCode?: boolean
}

interface MatchData {
  gameScores?: Array<{ team1: number; team2: number }>
  showQRCode?: boolean
}

export function useTennisMatchLogic({
  matchId,
  team1Color,
  team2Color,
  isController,
  showQRCode = true
}: UseTennisMatchLogicProps) {
  const [team1Games, setTeam1Games] = useState(0)
  const [team2Games, setTeam2Games] = useState(0)
  const [pendingSetCompletion, setPendingSetCompletion] = useState(false)
  const [pendingReset, setPendingReset] = useState(false)
  const [isQRCodeVisible, setQRCodeVisible] = useState(showQRCode)
  const [lastScoringTeam, setLastScoringTeam] = useState<'team1' | 'team2' | null>(null)
  const setCompletionTimer = useRef<NodeJS.Timeout | null>(null)

  const handleCommand = (commands: string[]) => {
    if (!isController) return

    const joined = commands.join(' ')
    const command = joined.toLowerCase()

    if (command.includes(team1Color)) {
      update(ref(database, `matches/${matchId}`), { team1Games: team1Games + 1 })
      setTeam1Games((prev) => prev + 1)
      setLastScoringTeam('team1')
    } else if (command.includes(team2Color)) {
      update(ref(database, `matches/${matchId}`), { team2Games: team2Games + 1 })
      setTeam2Games((prev) => prev + 1)
      setLastScoringTeam('team2')
    } else if (command.includes('undo')) {
      update(ref(database, `matches/${matchId}`), { team1Games: 0, team2Games: 0 })
      setTeam1Games(0)
      setTeam2Games(0)
    }
    // Add more logic as needed
  }

  function handleSetCompletion() {
    if (setCompletionTimer.current) clearTimeout(setCompletionTimer.current)

    setPendingSetCompletion(true)

    setCompletionTimer.current = setTimeout(() => {
      update(ref(database, `matches/${matchId}`), {
        team1Games: 0,
        team2Games: 0,
        events: {
          ...((lastScoringTeam === 'team1')
            ? { [`set-${Date.now()}`]: { team: 'team1', points: -1, timestamp: Date.now(), type: 'set' } }
            : { [`set-${Date.now()}`]: { team: 'team2', points: -1, timestamp: Date.now(), type: 'set' } }),
        },
      })

      setTeam1Games(0)
      setTeam2Games(0)
      setPendingSetCompletion(false)
      setLastScoringTeam(null)
    }, 2000)
  }

  function handleGameReset() {
    if (setCompletionTimer.current) clearTimeout(setCompletionTimer.current)

    setPendingReset(true)

    setCompletionTimer.current = setTimeout(() => {
      update(ref(database, `matches/${matchId}`), {
        team1Games: 0,
        team2Games: 0,
        events: {
          ...((lastScoringTeam === 'team1')
            ? { [`game-${Date.now()}`]: { team: 'team1', points: -1, timestamp: Date.now(), type: 'game' } }
            : { [`game-${Date.now()}`]: { team: 'team2', points: -1, timestamp: Date.now(), type: 'set' } }),
        },
      })

      setTeam1Games(0)
      setTeam2Games(0)
      setPendingReset(false)
      setLastScoringTeam(null)
    }, 1500)
  }

  useEffect(() => {
    return () => {
      if (setCompletionTimer.current) clearTimeout(setCompletionTimer.current)
    }
  }, [])

  useEffect(() => {
    const unsubscribe = subscribeToMatch(matchId, (data: MatchData) => {
      if (data.showQRCode !== undefined && data.showQRCode !== isQRCodeVisible) {
        setQRCodeVisible(data.showQRCode)
      }
      
      // Calculate games from gameScores array (handle both populated and empty arrays)
      if (data.gameScores !== undefined) {
        const team1Wins = data.gameScores.filter((score) => score.team1 > score.team2).length
        const team2Wins = data.gameScores.filter((score) => score.team2 > score.team1).length
        setTeam1Games(team1Wins)
        setTeam2Games(team2Wins)
      } else {
        // If gameScores is undefined, reset games to zero
        setTeam1Games(0);
        setTeam2Games(0);
      }
    })

    return () => {
      unsubscribe()
    }
  }, [matchId])

  return {
    team1Games,
    team2Games,
    pendingSetCompletion,
    pendingReset,
    isQRCodeVisible,
    setQRCodeVisible,
    lastScoringTeam,
    handleCommand,
    handleSetCompletion,
    handleGameReset,
  }
} 